import { Component, Vue } from 'vue-property-decorator';
import { CsvDownload, CropsIncomeOutgoResponse } from '@/types/APIResponse';
import { CropsIncomeOutgo, CropsIncomeOutgoOutline, AgriFieldIncomeOutgoTotalData } from '@/types/Component';
import IncomeAndOutgoChart from '@/components/IncomeAndOutgo/IncomeAndOutgoChart/IncomeAndOutgoChart.vue';
import { toCamel } from "snake-camel";

@Component({
    filters: {
        applyNumberFixed(value: number): string | null {
            return Number(value).toFixed(3).toLocaleString()
        },
        changeNumberFormat(value: number): string | null {
            return Math.round(value).toLocaleString().replace(/^-/, '▲')
        }
    },
    components: {
        IncomeAndOutgoChart
    }
})

export default class IncomeAndOutgoCrops extends Vue {

    private responseItems: Array<CropsIncomeOutgoOutline> = []
    private is10aView = false
    private isChartView = false
    private sortByIncomeIsAsc = false
    private sortByOutgoIsAsc = false
    private sortByAllIsAsc = false
    private sortByDisposalIsAsc = false
    private currentSortKey: 'crops' | 'income' | 'outgo' | 'total' = 'crops'
    
    private startDateInput = ''
    private endDateInput = ''
    private sortType = "";

    created() {
        this.startDateInput = this.$parent.$data.startDate
        this.endDateInput = this.$parent.$data.endDate
        this.getIncomeAndOutgo()
    }

    private getIncomeAndOutgo() {
        this.$apiClient.doRequest('get', `/admin/income_and_outgo/crops?start_date=${this.startDateInput}&end_date=${this.endDateInput}`).then((ret:CropsIncomeOutgoResponse) => {
            this.responseItems = (toCamel(ret) as CropsIncomeOutgo).outlines
            this.$parent.$data.startDate = this.startDateInput
            this.$parent.$data.endDate = this.endDateInput
        })
    }

    /**
     * 圃場毎の行データ。10a単位換算はここで行われる
     */
    get incomeAndOutgoItems() {
        return this.responseItems.map(i => (
            {
                cropId: i.cropId,
                cropName: i.cropName,
                cropPhonetic: i.cropPhonetic,
                plantedCount: i.plantedCount,
                plantedArea: i.plantedArea,
                income: this.apply10aView(i.income, i.plantedArea),
                workersOutgo: this.apply10aView(i.workersOutgo, i.plantedArea),
                machinesOutgo: this.apply10aView(i.machinesOutgo, i.plantedArea),
                chemicalsOutgo: this.apply10aView(i.chemicalsOutgo, i.plantedArea),
                fertilizersOutgo: this.apply10aView(i.fertilizersOutgo, i.plantedArea),
                materialsOutgo: this.apply10aView(i.materialsOutgo, i.plantedArea),
                seedlingsOutgo: this.apply10aView(i.seedlingsOutgo, i.plantedArea),
                outgo: this.apply10aView(i.outgo, i.plantedArea),
                total: this.apply10aView(i.total, i.plantedArea),
                disposalAmount: this.apply10aView(i.disposalAmount, i.plantedArea)
            }
        ))
    }

    /**
     * 合計行のデータ取得
     */
    get totalData(): AgriFieldIncomeOutgoTotalData | null {

        let totalPlantedCount = 0
        let totalPlantedArea = 0
        let totalIncome = 0
        let totalOutgo = 0
        let totalWorkersOutgo = 0
        let totalMachinesOutgo = 0
        let totalChemicalsOutgo = 0
        let totalFertilizersOutgo = 0
        let totalMaterialsOutgo = 0
        let totalSeedlingsOutgo = 0
        let totalDisposalAmounts = 0

        this.incomeAndOutgoItems.map((i) => {
            totalPlantedCount += i.plantedCount
            totalPlantedArea += Number(i.plantedArea)
            totalIncome += i.income
            totalOutgo += i.outgo
            totalWorkersOutgo += i.workersOutgo
            totalMachinesOutgo += i.machinesOutgo
            totalChemicalsOutgo += i.chemicalsOutgo
            totalFertilizersOutgo += i.fertilizersOutgo
            totalMaterialsOutgo += i.materialsOutgo
            totalSeedlingsOutgo += i.seedlingsOutgo
            totalDisposalAmounts += i.disposalAmount
        })
        return {
            totalPlantedCount,
            totalPlantedArea,
            totalIncome,
            totalOutgo,
            totalWorkersOutgo,
            totalMachinesOutgo,
            totalChemicalsOutgo,
            totalFertilizersOutgo,
            totalMaterialsOutgo,
            totalSeedlingsOutgo,
            totalDisposalAmounts
        }
    }

    /**
     * 10a単位の計算
     * @param amount 
     * @param plantedArea 
     */
    apply10aView(amount: number, plantedArea: number) {
        return this.is10aView? (10/plantedArea) * amount | 0 : amount
    }

    /**
     * 10a単位ビューへの切り替え
     */
    change10aView(is10aView: boolean) {
        this.is10aView = is10aView
        // 現在のソートキーに合わせたソートメソッドを呼ぶ
        const execSort = {
            income: this.sortByIncome,
            outgo: this.sortByOutgo,
            total: this.sortByAll,
            crops: () => { return }
        }
        execSort[this.currentSortKey](false)
    }

    /**
     * グラフビューへの切り替え
     */
    changeChartView(isChartView: boolean) {
        this.isChartView = isChartView
    }

    sortByIncome(isSwitchAsc?: boolean) {
        this.currentSortKey = 'income'
        // 昇順/降順の切替
        this.sortByIncomeIsAsc = isSwitchAsc ? !this.sortByIncomeIsAsc : this.sortByIncomeIsAsc
        this.responseItems.sort((a, b) => {
            const valueA = this.apply10aView(a.income, a.plantedArea)
            const valueB = this.apply10aView(b.income, b.plantedArea)
            return this.sortByIncomeIsAsc ? valueA - valueB : valueB - valueA
        })
    }

    sortByOutgo(isSwitchAsc?: boolean) {
        this.currentSortKey = 'outgo'
        // 昇順/降順の切替
        this.sortByOutgoIsAsc = isSwitchAsc ? !this.sortByOutgoIsAsc : this.sortByOutgoIsAsc
        this.responseItems.sort((a, b) => {
            const valueA = this.apply10aView(a.outgo, a.plantedArea)
            const valueB = this.apply10aView(b.outgo, b.plantedArea)
            return this.sortByOutgoIsAsc ? valueA - valueB : valueB - valueA
        })
    }

    sortByAll(isSwitchAsc?: boolean) {
        this.currentSortKey = 'total'
        // 昇順/降順の切替
        this.sortByAllIsAsc = isSwitchAsc ? !this.sortByAllIsAsc : this.sortByAllIsAsc
        this.responseItems.sort((a, b) => {
            const valueA = this.apply10aView(a.total, a.plantedArea)
            const valueB = this.apply10aView(b.total, b.plantedArea)
            return this.sortByAllIsAsc ? valueA - valueB : valueB - valueA
        })
    }
    sortByDisposal(isSwitchAsc?:boolean) {
        this.sortByDisposalIsAsc = isSwitchAsc ? !this.sortByDisposalIsAsc : this.sortByDisposalIsAsc
        this.responseItems.sort((a, b) => {
            const valueA = this.apply10aView(a.disposalAmount, a.plantedArea)
            const valueB = this.apply10aView(b.disposalAmount, b.plantedArea)
            return this.sortByDisposalIsAsc ? (valueA - valueB) : (valueB - valueA)

        })
    }
    exportCsv() {
        this.$apiClient.getRequest( `/admin/income_and_outgo/crops/csv_export?start_date=${this.startDateInput}&end_date=${this.endDateInput}`).then((res: CsvDownload) => {
            this.$apiClient.csvDownload(res)
        })
    }
    openSideBar = () => {
        const isResponsive = window.matchMedia('(max-width: 1000px)').matches;
        if (!isResponsive) {
            return; 
        }
        if (document.body.classList.contains('sideopen')) {
            document.body.classList.remove('sideopen')
        } else {
            document.body.classList.add('sideopen')
        }
    }
    onSortChange() {
      switch (this.sortType) {
        case "income":
          this.sortByIncome(true)
          break
        case "outgo":
          this.sortByOutgo(true)
          break
        case "all":
          this.sortByAll(true)
          break
        case "disposal":
          this.sortByDisposal(true)
          break
      }
    }
}